import { Moon, Sun, Monitor, MessageSquare, PanelLeft, PanelLeftClose } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { useUIStore } from '@/state/uiStore';
import { cn } from '@/lib/cn';

type AuthStatus =
  | { kind: 'pending' }
  | { kind: 'ok' }
  | { kind: 'error'; message: string };

export function Header({ auth }: { auth: AuthStatus }) {
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const sidebarPinned = useUIStore((s) => s.sidebarOpen);
  const toggleChat = useUIStore((s) => s.toggleChat);
  const chatOpen = useUIStore((s) => s.chatOpen);
  const { resolvedTheme } = useTheme();
  const [themeMounted, setThemeMounted] = useState(false);
  useEffect(() => setThemeMounted(true), []);
  // The icon-only logo has soft transparent edges that fade into white
  // backgrounds. In light mode swap to the full mark, which has dark text
  // baked in and reads cleanly. Default to the icon pre-mount to avoid a
  // flash of the wrong logo on dark systems.
  const useFullMark = themeMounted && resolvedTheme === 'light';
  const logoSrc = `${import.meta.env.BASE_URL}branding/${useFullMark ? 'gto-full.png' : 'gto-icon.png'}`;

  const statusLabel =
    auth.kind === 'pending' ? 'Connecting…' : auth.kind === 'ok' ? 'Connected' : 'Offline';
  const statusDot =
    auth.kind === 'pending'
      ? 'bg-yellow-400'
      : auth.kind === 'ok'
        ? 'bg-emerald-500'
        : 'bg-red-500';

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-background px-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleSidebar}
          className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label={sidebarPinned ? 'Collapse sidebar' : 'Pin sidebar open'}
          title={sidebarPinned ? 'Collapse sidebar (Ctrl+B)' : 'Pin sidebar open (Ctrl+B)'}
        >
          {sidebarPinned ? (
            <PanelLeftClose className="size-4" />
          ) : (
            <PanelLeft className="size-4" />
          )}
        </button>
        <div className="ml-1 flex items-center gap-2">
          <img
            src={logoSrc}
            alt="GTO"
            className="size-12 rounded-md object-contain"
            draggable={false}
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight text-foreground">
              GTO Agentic GIS
            </span>
            {!useFullMark && (
              <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-foreground/70">
                by Geotech Overseas
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground">
          <span className={cn('size-2 rounded-full', statusDot)} aria-hidden="true" />
          {statusLabel}
        </div>
        <ThemeSwitch />
        <button
          type="button"
          onClick={toggleChat}
          className={cn(
            'flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent',
            chatOpen && 'bg-accent text-accent-foreground',
          )}
          aria-label="Toggle chat"
          title="Toggle chat (Ctrl+J)"
        >
          <MessageSquare className="size-3.5" />
          Chat
        </button>
      </div>
    </header>
  );
}

function ThemeSwitch() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const current = mounted ? (theme ?? 'system') : 'system';
  const cycle = () => {
    const next = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    setTheme(next);
  };
  const Icon = current === 'light' ? Sun : current === 'dark' ? Moon : Monitor;
  return (
    <button
      type="button"
      onClick={cycle}
      className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      aria-label={`Theme: ${current}`}
      title={`Theme: ${current}`}
    >
      <Icon className="size-4" />
    </button>
  );
}
